import { useState, useEffect } from "react";
import {
  Plus,
  Search,
  Edit,
  Trash2,
  Shield,
  X,
  Download,
  Users,
  Server,
  Settings,
  AlertTriangle,
} from "lucide-react";
import Editor from "@monaco-editor/react";
import type { ServicePolicy } from "@/types/admin";
import adminService from "@/services/admin-service";
import yaml from "js-yaml";
import { Card, CardBody } from "@/components/ui/Card";
import { useAuthStore } from "@/stores/auth-store";

interface EnhancedPolicy extends ServicePolicy {
  scope?: "service_account" | "team" | "global";
  allowed_services?: string[] | string;
  rate_limit?: number;
  quota_multiplier?: number;
  description?: string;
}

export default function PoliciesPage() {
  const { isOrgAdmin } = useAuthStore();
  const canManage = isOrgAdmin();

  const [policies, setPolicies] = useState<EnhancedPolicy[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [showYamlModal, setShowYamlModal] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState<EnhancedPolicy | null>(
    null,
  );
  const [yamlContent, setYamlContent] = useState("");
  const [filterScope, setFilterScope] = useState<string>("all");

  useEffect(() => {
    loadPolicies();
  }, []);

  const loadPolicies = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await adminService.listPolicies();
      const enhanced = Array.isArray(data) ? data.map(enhancePolicy) : [];
      setPolicies(enhanced);
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to load policies");
      console.error(err);
      setPolicies([]);
    } finally {
      setLoading(false);
    }
  };

  const enhancePolicy = (policy: ServicePolicy): EnhancedPolicy => {
    const config = policy.config || {};
    return {
      ...policy,
      scope:
        config.scope ||
        (policy.service_name.includes("@") ? "service_account" : "team"),
      allowed_services: config.allowed_services || [],
      rate_limit: config.rate_limit,
      quota_multiplier: config.quota_multiplier,
      description: config.description,
    };
  };

  const policyToYaml = (policy: EnhancedPolicy): string => {
    const config = policy.config || {};
    const policyObj: any = {
      [policy.service_name]: {
        target_service: config.target_service || "*",
        allowed_endpoints: config.allowed_services || ["*"],
      },
    };

    if (policy.rate_limit || config.rate_limit) {
      policyObj[policy.service_name].rate_limit =
        policy.rate_limit || config.rate_limit;
    }
    if (policy.quota_multiplier || config.quota_multiplier) {
      policyObj[policy.service_name].quota_multiplier =
        policy.quota_multiplier || config.quota_multiplier;
    }
    if (policy.description || config.description) {
      policyObj[policy.service_name].description =
        policy.description || config.description;
    }

    return yaml.dump(policyObj, { indent: 2 });
  };

  const handleCardClick = (policy: EnhancedPolicy) => {
    setEditingPolicy(policy);
    setYamlContent(policyToYaml(policy));
    setShowYamlModal(true);
  };

  const handleCreate = () => {
    setEditingPolicy(null);
    const template = {
      "service-account@project.iam.gserviceaccount.com": {
        target_service: "llm",
        allowed_endpoints: ["generate", "embed"],
        rate_limit: 500,
        quota_multiplier: 1.0,
        description: "Service account authorization policy",
      },
    };
    setYamlContent(yaml.dump(template, { indent: 2 }));
    setShowYamlModal(true);
  };

  const handleSaveYaml = async () => {
    try {
      setSaving(true);
      setError(null);
      const parsed: any = yaml.load(yamlContent);
      const serviceName = Object.keys(parsed)[0];
      const config = parsed[serviceName];

      // Validate required fields
      if (!config.target_service) {
        setError("target_service is required in the policy configuration");
        setSaving(false);
        return;
      }
      if (!config.allowed_endpoints) {
        setError("allowed_endpoints is required in the policy configuration");
        setSaving(false);
        return;
      }

      if (editingPolicy) {
        await adminService.updatePolicy(editingPolicy.id, {
          config: config,
        });
      } else {
        await adminService.createPolicy({
          service_name: serviceName,
          policy_type: "authorization",
          config: config,
          enabled: true,
        });
      }
      setShowYamlModal(false);
      loadPolicies();
    } catch (err: any) {
      setError(err.message || "Failed to save policy");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (policy: EnhancedPolicy, e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();

    const confirmed = window.confirm(
      `Are you sure you want to delete the policy for "${policy.service_name}"?\n\nThis action cannot be undone.`,
    );

    if (!confirmed) return;

    try {
      await adminService.deletePolicy(policy.id);
      loadPolicies();
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to delete policy");
    }
  };

  const handleToggle = async (policy: EnhancedPolicy, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await adminService.togglePolicy(policy.id, !policy.enabled);
      loadPolicies();
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to toggle policy");
    }
  };

  const handleDownload = () => {
    const serviceAccounts: any = {};
    const teams: any = {};
    const settings: any = {
      default_rate_limit: 500,
      enable_team_policies: true,
      deny_by_default: true,
    };

    policies.forEach((policy) => {
      const config: any = {
        allowed_services: policy.allowed_services || "*",
      };
      if (policy.rate_limit) config.rate_limit = policy.rate_limit;
      if (policy.quota_multiplier)
        config.quota_multiplier = policy.quota_multiplier;
      if (policy.description) config.description = policy.description;

      if (policy.scope === "service_account") {
        serviceAccounts[policy.service_name] = config;
      } else if (policy.scope === "team") {
        teams[policy.service_name] = config;
      }
    });

    const yamlStr = yaml.dump(
      {
        ...serviceAccounts,
        teams: teams,
        settings: settings,
      },
      { indent: 2 },
    );

    const blob = new Blob([yamlStr], { type: "text/yaml" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "service_policies.yaml";
    a.click();
  };

  const filteredPolicies = Array.isArray(policies)
    ? policies.filter((policy) => {
        const matchesSearch =
          policy.service_name
            .toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          policy.description?.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesScope =
          filterScope === "all" || policy.scope === filterScope;
        return matchesSearch && matchesScope;
      })
    : [];

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Enhanced Header */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-purple-600 via-pink-600 to-red-600 p-8 text-white">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="relative flex items-center justify-between">
          <div>
            <h1 className="mb-2 text-4xl font-bold">
              Service Authorization Policies
            </h1>
            <p className="text-lg text-purple-100">
              Manage service-to-service and team-based authorization with YAML
            </p>
          </div>
          <div className="flex items-center gap-4">
            <div className="hidden rounded-xl bg-white/10 px-4 py-2 backdrop-blur-sm sm:block">
              <p className="text-sm text-purple-100">Total Policies</p>
              <p className="text-2xl font-bold">{filteredPolicies.length}</p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleDownload}
                className="inline-flex items-center gap-2 rounded-xl border border-white/20 bg-white/10 px-4 py-2 text-sm font-semibold text-white backdrop-blur-sm transition-colors hover:bg-white/20"
              >
                <Download className="h-4 w-4" />
                Export YAML
              </button>
              {canManage && (
                <button
                  onClick={handleCreate}
                  className="inline-flex items-center gap-2 rounded-xl bg-white/20 px-4 py-3 text-sm font-semibold text-white backdrop-blur-sm transition-colors hover:bg-white/30"
                >
                  <Plus className="h-4 w-4" />
                  Create Policy
                </button>
              )}
            </div>
          </div>
        </div>
        {/* Decorative elements */}
        <div className="absolute -right-4 -top-4 h-24 w-24 rounded-full bg-white/10"></div>
        <div className="absolute -bottom-4 -right-12 h-32 w-32 rounded-full bg-white/5"></div>
      </div>

      {/* Error Display */}
      {error && (
        <Card className="border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20">
          <CardBody>
            <div className="flex items-center space-x-3">
              <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400" />
              <div className="flex-1">
                <p className="text-sm font-medium text-red-800 dark:text-red-200">
                  Error
                </p>
                <p className="text-sm text-red-600 dark:text-red-400">
                  {error}
                </p>
              </div>
              <button
                onClick={() => setError(null)}
                className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-200"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </CardBody>
        </Card>
      )}

      {/* Search and Filters */}
      <Card>
        <CardBody>
          <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
            <div className="relative max-w-md flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search policies by name or description..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full rounded-xl border border-gray-200 bg-gray-50/50 py-3 pl-10 pr-4 text-sm placeholder-gray-400 transition-all focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-700 dark:bg-gray-800/50 dark:text-white dark:placeholder-gray-500"
              />
            </div>
            <div className="flex items-center gap-3">
              <select
                value={filterScope}
                onChange={(e) => setFilterScope(e.target.value)}
                className="rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm transition-all focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
              >
                <option value="all">All Policies</option>
                <option value="service_account">Service Accounts</option>
                <option value="team">Team Policies</option>
                <option value="global">Global Settings</option>
              </select>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                {filteredPolicies.length} policies
              </div>
            </div>
          </div>
        </CardBody>
      </Card>

      <div className="grid gap-6 lg:grid-cols-2 xl:grid-cols-3">
        {filteredPolicies.map((policy) => (
          <div
            key={policy.id}
            onClick={() => handleCardClick(policy)}
            className="group relative cursor-pointer overflow-hidden rounded-xl border border-gray-200/60 bg-gradient-to-br from-white via-white to-gray-50/50 shadow-lg backdrop-blur-sm transition-all duration-300 hover:scale-[1.02] hover:border-purple-500/30 hover:shadow-xl dark:border-gray-700/60 dark:from-gray-800 dark:via-gray-800 dark:to-gray-900/50 dark:shadow-gray-900/25 dark:hover:border-purple-400/30"
          >
            {/* Gradient Overlay */}
            <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 via-transparent to-blue-500/5 opacity-0 transition-opacity duration-300 group-hover:opacity-100" />

            {/* Content */}
            <div className="relative p-6">
              {/* Header */}
              <div className="mb-4 flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div
                    className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg shadow-lg ${
                      policy.scope === "service_account"
                        ? "bg-gradient-to-br from-blue-500 to-blue-600"
                        : policy.scope === "team"
                          ? "bg-gradient-to-br from-purple-500 to-purple-600"
                          : "bg-gradient-to-br from-gray-500 to-gray-600"
                    }`}
                  >
                    {policy.scope === "service_account" ? (
                      <Server className="h-5 w-5 text-white" />
                    ) : policy.scope === "team" ? (
                      <Users className="h-5 w-5 text-white" />
                    ) : (
                      <Settings className="h-5 w-5 text-white" />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3
                      className="truncate text-sm font-bold text-gray-900 transition-colors group-hover:text-purple-600 dark:text-white dark:group-hover:text-purple-400"
                      title={policy.service_name}
                    >
                      {policy.service_name}
                    </h3>
                    <div className="mt-1 flex items-center gap-2">
                      <div
                        className={`h-2 w-2 rounded-full ${policy.enabled ? "bg-green-500 shadow-lg shadow-green-500/30" : "bg-gray-400 dark:bg-gray-500"}`}
                      />
                      <span className="text-xs font-medium capitalize text-gray-600 dark:text-gray-400">
                        {policy.scope?.replace("_", " ") || "Service Account"}
                      </span>
                    </div>
                  </div>
                </div>
                <button
                  onClick={(e) => handleToggle(policy, e)}
                  className={`relative inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition-all duration-200 ${
                    policy.enabled
                      ? "bg-green-100 text-green-700 shadow-sm hover:bg-green-200 hover:shadow-md dark:bg-green-900/30 dark:text-green-400 dark:hover:bg-green-900/40"
                      : "bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-400 dark:hover:bg-gray-600"
                  }`}
                >
                  {policy.enabled && <Shield className="h-3 w-3" />}
                  {policy.enabled ? "Active" : "Inactive"}
                </button>
              </div>

              {/* Description */}
              {policy.description && (
                <p className="mb-4 line-clamp-2 text-sm leading-relaxed text-gray-600 dark:text-gray-300">
                  {policy.description}
                </p>
              )}

              {/* Services Info */}
              <div className="mb-4 flex items-center gap-3 rounded-lg border border-gray-200/60 bg-gray-50/80 p-3 dark:border-gray-600/60 dark:bg-gray-700/50">
                <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-600 dark:to-gray-700">
                  <Server className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="mb-0.5 text-xs font-medium text-gray-500 dark:text-gray-400">
                    Allowed Services
                  </div>
                  <div className="truncate font-mono text-sm text-gray-900 dark:text-white">
                    {Array.isArray(policy.allowed_services)
                      ? policy.allowed_services.length > 2
                        ? `${policy.allowed_services.slice(0, 2).join(", ")}...`
                        : policy.allowed_services.join(", ")
                      : policy.allowed_services || "*"}
                  </div>
                </div>
              </div>

              {/* Policy Settings */}
              <div className="mb-6 space-y-3">
                {policy.rate_limit && (
                  <div className="flex items-center justify-between rounded-lg border border-blue-200/30 bg-gradient-to-r from-blue-50/50 to-indigo-50/50 p-2.5 dark:border-blue-700/30 dark:from-blue-900/20 dark:to-indigo-900/20">
                    <div className="text-xs font-medium text-gray-500 dark:text-gray-400">
                      Rate Limit
                    </div>
                    <div className="text-sm font-bold text-blue-700 dark:text-blue-400">
                      {policy.rate_limit} req/min
                    </div>
                  </div>
                )}

                {policy.quota_multiplier && (
                  <div className="flex items-center justify-between rounded-lg border border-purple-200/30 bg-gradient-to-r from-purple-50/50 to-pink-50/50 p-2.5 dark:border-purple-700/30 dark:from-purple-900/20 dark:to-pink-900/20">
                    <div className="text-xs font-medium text-gray-500 dark:text-gray-400">
                      Quota Multiplier
                    </div>
                    <div className="text-sm font-bold text-purple-700 dark:text-purple-400">
                      {policy.quota_multiplier}x
                    </div>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              {canManage ? (
                <div className="flex items-center gap-3">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCardClick(policy);
                    }}
                    className="group/btn flex flex-1 items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-purple-600 to-purple-700 px-4 py-2.5 text-sm font-semibold text-white shadow-lg transition-all duration-200 hover:from-purple-700 hover:to-purple-800 hover:shadow-xl"
                  >
                    <Edit className="h-4 w-4 transition-transform group-hover/btn:scale-110" />
                    Edit YAML
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      handleDelete(policy, e);
                    }}
                    className="rounded-lg border-2 border-red-200 bg-red-50 p-2.5 text-red-600 transition-all duration-200 hover:scale-110 hover:bg-red-100 hover:text-red-700 dark:border-red-800/60 dark:bg-red-900/20 dark:text-red-400 dark:hover:bg-red-900/30 dark:hover:text-red-300"
                    title="Delete policy"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <div className="py-2 text-center">
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Super Admin access required
                  </p>
                </div>
              )}
            </div>

            {/* Shimmer Effect */}
            <div className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/10 to-transparent transition-transform duration-1000 ease-out group-hover:translate-x-full" />
          </div>
        ))}
      </div>

      {filteredPolicies.length === 0 && (
        <Card>
          <CardBody>
            <div className="py-12 text-center">
              <div className="mx-auto mb-6 flex h-24 w-24 items-center justify-center rounded-full bg-gradient-to-br from-purple-100 to-pink-200 dark:from-purple-700 dark:to-pink-600">
                <Shield className="h-12 w-12 text-purple-600 dark:text-purple-300" />
              </div>
              <h3 className="mb-2 text-lg font-semibold text-gray-900 dark:text-white">
                No policies found
              </h3>
              <p className="mx-auto max-w-sm text-gray-500 dark:text-gray-400">
                {searchTerm || filterScope !== "all"
                  ? "Try adjusting your search term or filter to find what you're looking for."
                  : "Authorization policies control access between services and teams. Create your first policy to get started."}
              </p>
              {!searchTerm && filterScope === "all" ? (
                <button
                  onClick={handleCreate}
                  className="mt-6 inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-purple-500 to-pink-600 px-6 py-3 text-sm font-semibold text-white shadow-lg transition-colors hover:from-purple-600 hover:to-pink-700"
                >
                  <Plus className="h-4 w-4" />
                  Create Your First Policy
                </button>
              ) : (
                <button
                  onClick={() => {
                    setSearchTerm("");
                    setFilterScope("all");
                  }}
                  className="mt-4 inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400"
                >
                  Clear filters
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </CardBody>
        </Card>
      )}

      {/* YAML Editor Modal */}
      {showYamlModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-6 backdrop-blur-sm">
          <Card
            shadow="xl"
            className="flex max-h-[90vh] w-full max-w-5xl flex-col border-0 shadow-2xl"
          >
            <div className="relative">
              {/* Gradient header */}
              <div className="h-20 rounded-t-xl bg-gradient-to-r from-purple-500 to-pink-600">
                <div className="absolute -bottom-6 left-6">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-r from-purple-600 to-pink-700 text-white shadow-lg ring-4 ring-white dark:ring-gray-800">
                    <Shield className="h-6 w-6" />
                  </div>
                </div>
              </div>

              {/* Close button */}
              <button
                onClick={() => setShowYamlModal(false)}
                className="absolute right-4 top-4 rounded-full bg-white/20 p-2 text-white transition-colors hover:bg-white/30"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <CardBody className="flex min-h-0 flex-col pt-8">
              <div className="mb-8 flex-shrink-0">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                  {editingPolicy
                    ? `Edit Authorization Policy`
                    : "Create Authorization Policy"}
                </h2>
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                  Configure service authorization policies with YAML for
                  granular access control
                </p>
              </div>

              <div
                className="min-h-0 flex-1 space-y-6 overflow-y-auto pr-2 focus:outline-none"
                tabIndex={0}
              >
                <div>
                  <div className="mb-3 flex items-center justify-between">
                    <label className="text-sm font-semibold text-gray-900 dark:text-white">
                      Authorization Policy Configuration (YAML)
                    </label>
                    <span className="inline-flex items-center rounded-full bg-yellow-100 px-3 py-1 text-xs font-medium text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400">
                      YAML Format
                    </span>
                  </div>
                  <div className="overflow-hidden rounded-xl border-2 border-gray-200 shadow-inner dark:border-gray-700">
                    <Editor
                      height="450px"
                      defaultLanguage="yaml"
                      value={yamlContent}
                      onChange={(value) => setYamlContent(value || "")}
                      theme="vs-dark"
                      options={{
                        minimap: { enabled: false },
                        fontSize: 14,
                        lineNumbers: "on",
                        scrollBeyondLastLine: false,
                        automaticLayout: true,
                        tabSize: 2,
                        wordWrap: "on",
                        padding: { top: 16, bottom: 16 },
                        folding: true,
                        bracketPairColorization: { enabled: true },
                      }}
                    />
                  </div>
                  <div className="mt-3 rounded-xl border border-purple-200/30 bg-gradient-to-r from-purple-50 to-pink-50 p-4 dark:border-purple-700/30 dark:from-purple-900/20 dark:to-pink-900/20">
                    <div className="flex items-start space-x-3">
                      <Settings className="mt-0.5 h-5 w-5 flex-shrink-0 text-purple-600 dark:text-purple-400" />
                      <div className="flex-1">
                        <h4 className="mb-1 text-sm font-semibold text-purple-900 dark:text-purple-200">
                          Policy Configuration Guide
                        </h4>
                        <div className="space-y-2 text-sm text-purple-700 dark:text-purple-300">
                          <p>
                            Configure service-to-service authorization policies
                            using YAML format:
                          </p>
                          <ul className="ml-2 list-inside list-disc space-y-1">
                            <li>
                              <code className="rounded bg-purple-100 px-1 dark:bg-purple-800">
                                target_service
                              </code>
                              : Which service this policy applies to (e.g.,
                              "llm", "cot", "*" for all)
                            </li>
                            <li>
                              <code className="rounded bg-purple-100 px-1 dark:bg-purple-800">
                                allowed_endpoints
                              </code>
                              : List of allowed endpoints (e.g., ["generate",
                              "embed"] or ["*"] for all)
                            </li>
                            <li>
                              <code className="rounded bg-purple-100 px-1 dark:bg-purple-800">
                                rate_limit
                              </code>
                              : Optional requests per minute limit
                            </li>
                            <li>
                              <code className="rounded bg-purple-100 px-1 dark:bg-purple-800">
                                quota_multiplier
                              </code>
                              : Optional quota multiplier (default: 1.0)
                            </li>
                          </ul>
                          <p className="mt-2 text-xs">
                            Note: Create separate policies for each target
                            service. One service account can have multiple
                            policies.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    onClick={() => setShowYamlModal(false)}
                    className="flex-1 rounded-xl bg-gray-100 px-4 py-3 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveYaml}
                    disabled={saving}
                    className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-purple-500 to-pink-600 px-4 py-3 text-sm font-semibold text-white shadow-lg transition-all hover:from-purple-600 hover:to-pink-700 hover:shadow-xl disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {saving && (
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    )}
                    {saving
                      ? "Saving..."
                      : editingPolicy
                        ? "Update Policy"
                        : "Create Policy"}
                  </button>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>
      )}
    </div>
  );
}
